import React, { useState } from 'react';
import API_URL from '../config';

const mainColor = '#38b2ac';
const accentColor = '#48bb78';
const glassBg = 'rgba(255,255,255,0.55)';
const glassShadow = '0 8px 32px rgba(60, 60, 120, 0.18)';
const vibrantGradient = 'linear-gradient(120deg, #38b2ac 0%, #667eea 100%)';
const statsGlow = '0 0 16px 2px #38b2ac33, 0 2px 8px #48bb7833';
const fontFamily = 'Inter, Segoe UI, Arial, sans-serif';

const ScanRequestForm = () => {
  const [email, setEmail] = useState('');
  const [files, setFiles] = useState([]);
  const [proof, setProof] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [dragActiveProof, setDragActiveProof] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || files.length === 0 || !proof) {
      setError('Please fill in all fields and upload all required files.');
      return;
    }

    setLoading(true);
    setError('');

    const formData = new FormData();
    formData.append('email', email);
    files.forEach(file => formData.append('files', file));
    formData.append('proof', proof);

    try {
      const response = await fetch(`${API_URL}/api/scan-request`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Failed to submit scan request');
      }

      setSuccess(true);
      setEmail('');
      setFiles([]);
      setProof(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDrag = (e, isProof = false) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      isProof ? setDragActiveProof(true) : setDragActive(true);
    } else if (e.type === 'dragleave') {
      isProof ? setDragActiveProof(false) : setDragActive(false);
    }
  };

  const handleDrop = (e, isProof = false) => {
    e.preventDefault();
    e.stopPropagation();
    isProof ? setDragActiveProof(false) : setDragActive(false);

    const droppedFiles = [...e.dataTransfer.files];
    if (isProof) {
      if (droppedFiles.length > 0) {
        setProof(droppedFiles[0]);
      }
    } else {
      setFiles(prev => [...prev, ...droppedFiles]);
    }
  };

  const handleFileChange = (e, isProof = false) => {
    const selectedFiles = [...e.target.files];
    if (isProof) {
      if (selectedFiles.length > 0) {
        setProof(selectedFiles[0]);
      }
    } else {
      setFiles(prev => [...prev, ...selectedFiles]);
    }
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const removeProof = () => {
    setProof(null);
  };

  if (success) {
    return (
      <div style={{ minHeight: '100vh', background: vibrantGradient, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily }}>
        <div style={{ background: glassBg, padding: 40, borderRadius: 22, boxShadow: glassShadow, maxWidth: 500, width: '100%', textAlign: 'center', backdropFilter: 'blur(16px)', border: '1.5px solid #e0e7ff' }}>
          <div style={{ fontSize: 64, marginBottom: 20, animation: 'bounce 1s infinite' }}>🎉</div>
          <h2 style={{ color: mainColor, marginBottom: 20, fontWeight: 900, fontSize: '2rem' }}>Request Submitted!</h2>
          <p style={{ color: '#4a5568', marginBottom: 30, fontSize: '1.1rem', lineHeight: 1.6 }}>Your scan request has been submitted successfully. Wait for the email for the results.</p>
          <button onClick={() => setSuccess(false)} style={{ padding: '14px 32px', background: vibrantGradient, color: 'white', border: 'none', borderRadius: 12, fontWeight: 900, fontSize: '1.1rem', cursor: 'pointer', boxShadow: '0 2px 8px #38b2ac22', letterSpacing: '0.5px', transition: 'background 0.2s' }}>Submit Another Request</button>
          <style>{`
            @keyframes bounce {
              0%, 100% { transform: translateY(0); }
              50% { transform: translateY(-20px); }
            }
          `}</style>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: vibrantGradient, padding: '40px 20px', fontFamily }}>
      <div className="scan-form-container" style={{ maxWidth: 800, margin: '0 auto', background: glassBg, borderRadius: 22, boxShadow: glassShadow, padding: 40, backdropFilter: 'blur(16px)', border: '1.5px solid #e0e7ff' }}>
        <div style={{ textAlign: 'center', marginBottom: 30 }}>
          <h1 className="scan-form-title" style={{ color: mainColor, marginBottom: 10, fontWeight: 900, fontSize: '2.2rem', letterSpacing: '-1px' }}>Submit Scan Request</h1>
          <p className="scan-form-subtitle" style={{ color: '#4a5568', fontSize: '1.1rem', lineHeight: 1.6 }}>Upload your files and proof of payment to submit your scan request.</p>
          <div style={{ display: 'inline-block', background: '#e6fffa', padding: '8px 16px', borderRadius: 12, marginTop: 16, border: `2px solid ${mainColor}33` }}>
            <span style={{ color: mainColor, fontWeight: 800, fontSize: '1.1rem' }}>10 Pesos Per File</span>
          </div>
        </div>

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
          <div>
            <label className="scan-form-label" style={{ display: 'block', marginBottom: 8, color: '#2d3748', fontWeight: 700, fontSize: '1.1rem' }}>Email Address</label>
            <input
              className="scan-form-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={{ width: '100%', padding: '14px 18px', borderRadius: 12, border: '1.5px solid #e0e7ff', fontSize: '1.05rem', background: '#fff', boxShadow: '0 1px 4px #e0e7ff22', fontWeight: 500, outline: 'none', color: '#374151', transition: 'box-shadow 0.2s' }}
              placeholder="Enter your email address"
              required
            />
          </div>

          <div>
            <label className="scan-form-label" style={{ display: 'block', marginBottom: 8, color: '#2d3748', fontWeight: 700, fontSize: '1.1rem' }}>Files to Scan</label>
            <div
              className="scan-form-drag"
              onDragEnter={(e) => handleDrag(e, false)}
              onDragLeave={(e) => handleDrag(e, false)}
              onDragOver={(e) => handleDrag(e, false)}
              onDrop={(e) => handleDrop(e, false)}
              style={{
                border: `2px dashed ${dragActive ? mainColor : '#e0e7ff'}`,
                borderRadius: 12,
                padding: 30,
                textAlign: 'center',
                background: dragActive ? '#e6fffa' : '#fff',
                transition: 'all 0.2s',
                cursor: 'pointer',
              }}
            >
              <input
                type="file"
                multiple
                onChange={(e) => handleFileChange(e, false)}
                style={{ display: 'none' }}
                id="fileInput"
              />
              <label htmlFor="fileInput" style={{ cursor: 'pointer' }}>
                <div style={{ fontSize: 48, marginBottom: 16, color: mainColor }}>📄</div>
                <p style={{ color: '#4a5568', marginBottom: 8, fontSize: '1.1rem' }}>Drag and drop your files here</p>
                <p style={{ color: '#718096', fontSize: '0.95rem' }}>or click to browse</p>
              </label>
            </div>
            {files.length > 0 && (
              <div className="scan-form-filelist" style={{ marginTop: 16 }}>
                {files.map((file, index) => (
                  <div key={index} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#f8fafc', padding: '10px 16px', borderRadius: 8, marginBottom: 8 }}>
                    <span style={{ color: '#2d3748', fontWeight: 600 }}>{file.name}</span>
                    <button
                      type="button"
                      onClick={() => removeFile(index)}
                      style={{ background: 'none', border: 'none', color: '#e53e3e', cursor: 'pointer', fontSize: '1.2rem' }}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Payment QR Section */}
          <div className="scan-form-qr" style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            margin: '32px 0 18px 0',
            background: '#e6fffa',
            borderRadius: 16,
            padding: 24,
            border: `2px solid ${mainColor}33`,
          }}>
            <div style={{ fontWeight: 800, color: mainColor, fontSize: 22, marginBottom: 18, textAlign: 'center' }}>
              Scan to Pay via GCash or PayMaya
            </div>
            <img
              src="/scanqr.jpg"
              alt="GCash/PayMaya QR Code"
              style={{
                width: 'min(90vw, 320px)',
                height: 'min(90vw, 320px)',
                maxWidth: 320,
                maxHeight: 320,
                borderRadius: 18,
                border: `3px solid ${mainColor}`,
                boxShadow: '0 2px 16px #38b2ac22',
                objectFit: 'cover',
                display: 'block',
                marginBottom: 12,
              }}
            />
            <div style={{ color: '#374151', fontSize: '1.08rem', marginTop: 10, textAlign: 'center' }}>
              Please scan the QR code above to pay for your document scan.<br />
              <span style={{ color: accentColor, fontWeight: 700 }}>10 Pesos per file</span>.<br />
              After payment, upload your proof of payment below.
            </div>
          </div>

          <div>
            <label className="scan-form-label" style={{ display: 'block', marginBottom: 8, color: '#2d3748', fontWeight: 700, fontSize: '1.1rem' }}>Proof of Payment</label>
            <div
              className="scan-form-drag"
              onDragEnter={(e) => handleDrag(e, true)}
              onDragLeave={(e) => handleDrag(e, true)}
              onDragOver={(e) => handleDrag(e, true)}
              onDrop={(e) => handleDrop(e, true)}
              style={{
                border: `2px dashed ${dragActiveProof ? mainColor : '#e0e7ff'}`,
                borderRadius: 12,
                padding: 30,
                textAlign: 'center',
                background: dragActiveProof ? '#e6fffa' : '#fff',
                transition: 'all 0.2s',
                cursor: 'pointer',
              }}
            >
              <input
                type="file"
                onChange={(e) => handleFileChange(e, true)}
                style={{ display: 'none' }}
                id="proofInput"
              />
              <label htmlFor="proofInput" style={{ cursor: 'pointer' }}>
                <div style={{ fontSize: 48, marginBottom: 16, color: mainColor }}>💸</div>
                <p style={{ color: '#4a5568', marginBottom: 8, fontSize: '1.1rem' }}>Drag and drop your proof of payment here</p>
                <p style={{ color: '#718096', fontSize: '0.95rem' }}>or click to browse</p>
              </label>
            </div>
            {proof && (
              <div style={{ marginTop: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#f8fafc', padding: '10px 16px', borderRadius: 8 }}>
                  <span style={{ color: '#2d3748', fontWeight: 600 }}>{proof.name}</span>
                  <button
                    type="button"
                    onClick={removeProof}
                    style={{ background: 'none', border: 'none', color: '#e53e3e', cursor: 'pointer', fontSize: '1.2rem' }}
                  >
                    ×
                  </button>
                </div>
              </div>
            )}
          </div>

          {error && (
            <div style={{ background: '#fff5f5', color: '#e53e3e', padding: '12px 16px', borderRadius: 8, border: '1px solid #feb2b2' }}>
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            style={{
              padding: '16px 32px',
              background: vibrantGradient,
              color: 'white',
              border: 'none',
              borderRadius: 12,
              fontWeight: 900,
              fontSize: '1.1rem',
              cursor: loading ? 'not-allowed' : 'pointer',
              boxShadow: '0 2px 8px #38b2ac22',
              letterSpacing: '0.5px',
              transition: 'background 0.2s',
              opacity: loading ? 0.7 : 1,
            }}
          >
            {loading ? 'Submitting...' : 'Submit Request'}
          </button>
        </form>
      </div>
      <style>{`
        @media (max-width: 600px) {
          .scan-form-container {
            padding: 16px 2vw !important;
            border-radius: 10px !important;
            box-shadow: 0 2px 8px #38b2ac22 !important;
          }
          .scan-form-title {
            font-size: 1.3rem !important;
            margin-bottom: 6px !important;
          }
          .scan-form-subtitle {
            font-size: 1rem !important;
          }
          .scan-form-label {
            font-size: 1rem !important;
            margin-bottom: 4px !important;
          }
          .scan-form-input, .scan-form-btn {
            font-size: 1em !important;
            padding: 10px 12px !important;
            border-radius: 8px !important;
          }
          .scan-form-btn {
            width: 100% !important;
            margin-top: 10px !important;
          }
          .scan-form-drag {
            padding: 18px !important;
            font-size: 1em !important;
          }
          .scan-form-filelist {
            font-size: 0.98em !important;
            padding: 8px 8px !important;
          }
          .scan-form-qr {
            padding: 12px !important;
            border-radius: 10px !important;
            margin: 18px 0 10px 0 !important;
          }
          .scan-form-qr img {
            max-width: 90vw !important;
            height: auto !important;
          }
        }
      `}</style>
    </div>
  );
};

export default ScanRequestForm; 